/**
 * SideChat — RunAI WebOS'un ayrılmaz parçası.
 * - KAPATILAMAZ. Sadece pozisyon değişir (right/left/bottom).
 * - Sağ/sol → dikey kolon (~320px). Alt → yatay (~260px yükseklik).
 * - Aktif agent ile sohbet. Mesaj geçmişi ChatContext'te.
 * - Resize tutucuları + Fullscreen modu
 *
 * Rule 94: Modüler — SideChat sadece layout orchestrator.
 * İş mantığı hook'larda, render sub-component'lerde.
 */
import { useEffect, useState } from 'react'
import { API_URL, apiFetch } from '../../../config/api'
import { useAgents } from '../../../contexts/AgentContext'
import { useAuth } from '../../../contexts/AuthContext'
import { useChat } from '../../../contexts/ChatContext'
import { useResponsive } from '../../../hooks/useResponsive'
import { useVoiceMode } from '../../../hooks/useVoiceMode'
import { useSpeechInput } from '../ChatBar/useSpeechInput'
import { useTTS } from '../ChatBar/useTTS'
import { useDockPosition } from '../Dock/useDockPosition'
import { VoicePill } from '../../voice/VoicePill'
import ChatHeader from './ChatHeader'
import ChatInputBar from './ChatInputBar'
import ChatMessageList from './ChatMessageList'
import SideChatResizeHandle from './SideChatResizeHandle'
import SidePanelBar from './SidePanelBar'
import { SkillSidebar } from './SkillSidebar'
import { useChatEvents } from './useChatEvents'
import { useChatHistory } from './useChatHistory'
import { useChatSend } from './useChatSend'
import { useSideChatPosition } from './useSideChatPosition'
import { useSideChatSize } from './useSideChatSize'
import { useSlashCommandHandler } from './useSlashCommandHandler'

export default function SideChat() {
  // ── Layout state ──
  const { position, setPos } = useSideChatPosition()
  const { width, height, isFullscreen, saveWidth, saveHeight, toggleFullscreen } = useSideChatSize()
  const { position: dockPos } = useDockPosition()
  const { isMobile } = useResponsive()
  const [collapsed, setCollapsed] = useState(isMobile)
  const [input, setInput] = useState('')
  const [showSkills, setShowSkills] = useState(false)

  // ── Voice Mode (floating pill) ──
  const voiceMode = useVoiceMode()

  // ── Context ──
  const { activeAgent } = useAgents()
  const { user } = useAuth()
  const { chat, addMessage, updateLastMessage, loadHistory, historyLoaded } = useChat()

  // ── Load chat history from DB on mount ──
  useChatHistory(activeAgent?.id, loadHistory, historyLoaded)

  // ── Voice ──
  const osLang = localStorage.getItem('filiz_lang') || 'en'
  const { listening, toggle: toggleMic } = useSpeechInput((text) => {
    setInput(prev => prev + text)
  }, osLang)
  const { enabled: ttsEnabled, speak, toggle: toggleTTS } = useTTS(osLang)

  // ── Skills list (for autocomplete) ──
  const [skillsList, setSkillsList] = useState<Array<{ id: number; name: string }>>([])
  useEffect(() => {
    apiFetch(`${API_URL}/api/aicloud/skills/installed`)
      .then(r => r.json())
      .then((d: any) => setSkillsList(d.skills?.map((s: any) => ({ id: s.id, name: s.name })) ?? []))
      .catch(() => {})
  }, [])

  // ── Unified event listeners (skill results, task alerts, research, social, flzhub) ──
  useChatEvents(addMessage)

  // ── Agent OS control: mic + TTS toggle ──
  useEffect(() => {
    const micHandler = (e: Event) => {
      const state = (e as CustomEvent).detail as string
      if (state === 'on' && !listening) toggleMic()
      else if (state === 'off' && listening) toggleMic()
      else if (state === 'toggle') toggleMic()
    }
    const ttsHandler = (e: Event) => {
      const state = (e as CustomEvent).detail as string
      if (state === 'on' && !ttsEnabled) toggleTTS()
      else if (state === 'off' && ttsEnabled) toggleTTS()
      else if (state === 'toggle') toggleTTS()
    }
    window.addEventListener('runai:toggle-mic', micHandler)
    window.addEventListener('runai:toggle-tts', ttsHandler)
    return () => {
      window.removeEventListener('runai:toggle-mic', micHandler)
      window.removeEventListener('runai:toggle-tts', ttsHandler)
    }
  }, [listening, ttsEnabled, toggleMic, toggleTTS])

  // ── Brief state ──
  const [briefStates, setBriefStates] = useState<Record<number, 'pending' | 'approved' | 'editing'>>({})

  // ── Chat send (LLM call + twin + pulse) ──
  const { sending, sendMessage } = useChatSend({
    addMessage, updateLastMessage,
    agentId: activeAgent?.id,
    agentName: activeAgent?.agent_name,
    speak,
  })

  // ── Slash command handler ──
  const { handleSlashCommand } = useSlashCommandHandler({
    addMessage, updateLastMessage,
    setSending: () => {}, // sending is managed by useChatSend
    setCategory: () => {}, // categories removed — chat only
    speak,
    activeAgentName: activeAgent?.agent_name,
  })

  // ── Welcome message — only show if no history ──
  useEffect(() => {
    if (!user?.id || !historyLoaded) return
    const showWelcome = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/user/context`)
        const data = await res.json() as { welcome_sent?: boolean }
        if (!data.welcome_sent) {
          const welcomeMsg = `Ready! I'm your personal AI agent. Here's what I can do:
• FlzHub → family chat [flzhub]
• Skills → 159 skills available [store]
• Twin → I'll learn about you over time [twin]
What would you like to do?`
          addMessage({ role: 'assistant', content: welcomeMsg, ts: new Date().toLocaleTimeString(), agentName: activeAgent?.agent_name || 'Agent' })
          await apiFetch(`${API_URL}/api/user/welcome-sent`, { method: 'POST' })
        }
      } catch (e) {
        console.error('Welcome message error:', e)
      }
    }
    const timer = setTimeout(showWelcome, 300)
    return () => clearTimeout(timer)
  }, [user?.id, historyLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Brief approve ──
  const approveBrief = async (briefId: number, title: string) => {
    setBriefStates(prev => ({ ...prev, [briefId]: 'approved' }))
    try {
      const res = await apiFetch(`${API_URL}/api/tasks/brief/${briefId}/approve`, {
        method: 'POST', body: JSON.stringify({}), headers: { 'Content-Type': 'application/json' },
      })
      const data = await res.json() as { ok?: boolean; tasks_created?: number; error?: string }
      if (data.ok) {
        addMessage({
          role: 'assistant',
          content: `✅ ${data.tasks_created ?? 0} görev oluşturuldu, başlıyorum: "${title}"`,
          ts: new Date().toLocaleTimeString(),
          agentName: activeAgent?.agent_name ?? 'Agent', status: 'done',
        })
        window.dispatchEvent(new CustomEvent('runai:task-alert', { detail: { type: 'created', title } }))
      } else {
        addMessage({ role: 'assistant', content: `❌ Onaylanamadı: ${data.error ?? 'unknown'}`, ts: new Date().toLocaleTimeString(), agentName: 'Planner', status: 'done' })
      }
    } catch {
      addMessage({ role: 'assistant', content: '⚠ Onay başarısız (bağlantı).', ts: new Date().toLocaleTimeString(), agentName: 'Planner', status: 'done' })
    }
  }

  // ── Brief runner ──
  const runBrief = async (description: string) => {
    setInput('')
    addMessage({ role: 'user', content: `/brief ${description}`, ts: new Date().toLocaleTimeString() })
    addMessage({ role: 'assistant', content: '📋 Brief analiz ediliyor...', ts: new Date().toLocaleTimeString(), agentName: 'Planner', status: 'thinking' })
    try {
      const res = await apiFetch(`${API_URL}/api/tasks/brief`, {
        method: 'POST', body: JSON.stringify({ description }), headers: { 'Content-Type': 'application/json' },
      })
      const data = await res.json() as {
        ok?: boolean; error?: string; brief_id?: number; title?: string
        tasks?: Array<{ title: string; complexity?: number; description?: string; priority?: string }>
        estimated_hours?: number; goals?: string[]
      }
      if (!data.ok || !data.brief_id) {
        updateLastMessage({ content: `❌ Brief oluşturulamadı: ${data.error ?? 'unknown'}`, status: 'done' })
        return
      }
      const briefMarker = `__BRIEF__:${JSON.stringify({
        brief_id: data.brief_id, title: data.title ?? 'Brief',
        tasks: data.tasks ?? [], estimated_hours: data.estimated_hours ?? 0, goals: data.goals ?? [],
      })}`
      updateLastMessage({ content: briefMarker, status: 'done' })
      setBriefStates(prev => ({ ...prev, [data.brief_id!]: 'pending' }))
    } catch (e: any) {
      updateLastMessage({ content: `❌ Brief hatası: ${e?.message ?? 'connection'}`, status: 'done' })
    }
  }

  // ── Master send handler ──
  const send = async () => {
    const text = input.trim()
    if (!text || sending) return

    // "I want to build ..." → auto brief
    if (/^(i want to build|i want to make|i'd like to build|i would like to build)\b/i.test(text)) {
      await runBrief(text)
      return
    }

    // /brief special case (needs runBrief)
    if (text.startsWith('/brief ')) {
      await runBrief(text.slice(7).trim())
      setInput('')
      return
    }

    // Try slash command first
    setInput('')
    const handled = await handleSlashCommand(text)
    if (handled) return

    // Normal message → LLM
    await sendMessage(text)
  }

  // ── Layout calculation ──
  const isHorizontal = position === 'bottom'
  const containerStyle = isMobile
    ? buildMobileStyle(collapsed)
    : buildContainerStyle(position, dockPos, isFullscreen, collapsed, width, height, isHorizontal)

  return (
    <div className="os-side-chat" style={containerStyle}>
      {!isMobile && !isFullscreen && <SideChatResizeHandle position={position} onResize={saveWidth || saveHeight ? (w, h) => { if (w) saveWidth(w); if (h) saveHeight(h) } : () => {}} />}

      {/* Voice Mode Pill — portal to body */}
      <VoicePill active={voiceMode.isActive} onClose={voiceMode.stopVoice} />

      <ChatHeader
        position={position} setPos={setPos}
        collapsed={collapsed} setCollapsed={setCollapsed}
        isFullscreen={isFullscreen} toggleFullscreen={toggleFullscreen}
        showSkills={showSkills} onSkillsToggle={() => setShowSkills(v => !v)}
        onExport={() => {
          // G4: Export chat history as JSON
          const exportData = {
            exported_at: new Date().toISOString(),
            agent: activeAgent?.agent_name ?? 'Agent',
            messages: chat.map(m => ({
              role: m.role,
              content: m.content,
              timestamp: m.ts,
            }))
          }
          const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `chat-history-${new Date().toISOString().split('T')[0]}.json`
          a.click()
          URL.revokeObjectURL(url)
        }}
      />

      {/* AgentStrip removed — user has one instruct agent, no need to show family agents */}

      {/* Main area: chat + side panel bar */}
      {!collapsed && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', minHeight: 0 }}>
          {/* Chat column */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            <ChatMessageList
              chat={chat} isHorizontal={isHorizontal}
              briefStates={briefStates} onApproveBrief={approveBrief}
            />
            <ChatInputBar
              input={input} setInput={setInput}
              sending={sending} onSend={() => void send()}
              listening={listening} toggleMic={toggleMic}
              ttsEnabled={ttsEnabled} toggleTTS={toggleTTS}
              activeAgentName={activeAgent?.agent_name}
              skillsList={skillsList}
              onVoiceMode={voiceMode.startVoice}
              voiceModeActive={voiceMode.isActive}
            />
          </div>

          {/* Skills sidebar panel */}
          {showSkills && !isMobile && !isHorizontal && (
            <div style={{ width: 220, borderLeft: '1px solid var(--os-border)', flexShrink: 0, overflow: 'hidden' }}>
              <SkillSidebar onClose={() => setShowSkills(false)} />
            </div>
          )}

          {/* Side panel bar (Tasks, Research, Work, Skills) */}
          {!isMobile && !isHorizontal && (
            <SidePanelBar
              panelWidth={280}
              chatPosition={position}
              chatTop={position === 'right' || position === 'left'
                ? (dockPos === 'top' ? 124 : 44)
                : 44}
              chatBottom={dockPos === 'bottom' ? 80 : 8}
              chatRight={position === 'right' ? (dockPos === 'right' ? 80 : 8) : undefined}
              chatLeft={position === 'left' ? (dockPos === 'left' ? 80 : 8) : undefined}
              chatWidth={collapsed ? 48 : width}
            />
          )}
        </div>
      )}
    </div>
  )
}

/* ── Mobile bottom sheet style ── */
function buildMobileStyle(collapsed: boolean): React.CSSProperties {
  return {
    position: 'fixed',
    left: 0, right: 0, bottom: 48,
    height: collapsed ? 44 : '70vh',
    background: 'var(--os-surface)',
    backdropFilter: 'blur(28px)',
    WebkitBackdropFilter: 'blur(28px)',
    color: 'var(--os-text)',
    zIndex: 1500,
    display: 'flex',
    flexDirection: 'column',
    transition: 'height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: 'var(--os-card-shadow)',
    overflow: 'hidden',
    borderTop: '1px solid var(--os-hover)',
    borderRadius: '12px 12px 0 0',
  }
}

/* ── Container style builder ── */
function buildContainerStyle(
  position: string, dockPos: string, isFullscreen: boolean,
  collapsed: boolean, width: number, height: number, _isHorizontal: boolean,
): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'fixed',
    background: 'var(--os-surface)',
    backdropFilter: 'blur(28px)',
    WebkitBackdropFilter: 'blur(28px)',
    color: 'var(--os-text)',
    zIndex: 1500,
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: 'var(--os-card-shadow)',
    overflow: 'hidden',
  }

  if (isFullscreen) {
    return { ...base, top: 30, left: 0, right: 0, bottom: 0, borderTop: '1px solid var(--os-hover)' }
  }

  const DOCK_SIZE = 80
  const MENUBAR = 44

  if (position === 'right') return {
    ...base, top: dockPos === 'top' ? MENUBAR + DOCK_SIZE : MENUBAR,
    right: dockPos === 'right' ? DOCK_SIZE : 8,
    bottom: dockPos === 'bottom' ? DOCK_SIZE : 8,
    width: collapsed ? 48 : width,
    borderLeft: '1px solid var(--os-hover)', borderRadius: 12,
  }
  if (position === 'left') return {
    ...base, top: dockPos === 'top' ? MENUBAR + DOCK_SIZE : MENUBAR,
    left: dockPos === 'left' ? DOCK_SIZE : 8,
    bottom: dockPos === 'bottom' ? DOCK_SIZE : 8,
    width: collapsed ? 48 : width,
    borderRight: '1px solid var(--os-hover)', borderRadius: 12,
  }
  if (position === 'center') return {
    ...base, top: MENUBAR + 20, left: '50%', transform: 'translateX(-50%)',
    bottom: dockPos === 'bottom' ? DOCK_SIZE : 80,
    width: collapsed ? 48 : Math.min(width, 500),
    borderRadius: 16, border: '1px solid var(--os-border)',
  }
  // bottom
  return {
    ...base, left: dockPos === 'left' ? DOCK_SIZE : 8,
    right: dockPos === 'right' ? DOCK_SIZE : 8,
    bottom: dockPos === 'bottom' ? DOCK_SIZE : 8,
    height: collapsed ? 48 : height,
    borderTop: '1px solid var(--os-hover)', borderRadius: '12px 12px 0 0',
  }
}
